/* eslint-disable @typescript-eslint/no-explicit-any */
// @ts-nocheck
import React, { useMemo, useState } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  CartesianGrid,
} from 'recharts';
import { D, ROUTES, GMAPS, LD_COLORS } from '@/data/dashboardData';
import { useMapRegions } from '@/context/MapRegionsContext';
import { LD_NAMES } from '@/lib/computeStats';
import { BenchStat, Breadcrumb } from '@/components/shared';
import { ViewHeader } from '@/components/ViewHeader';
import { downloadRouteCsv, triggerBrowserDownload } from '@/api/exports';

export const RoutesView: React.FC<any> = ({ onDrill, onBack }) => {
  const [routeIdInput, setRouteIdInput] = useState('');
  const [downloadError, setDownloadError] = useState<string | null>(null);
  const [downloading, setDownloading] = useState(false);
  const { catalog: regions } = useMapRegions();

  const routeStats = {
    totalDoors: ROUTES.total_targetable,
    totalTurfs: ROUTES.total_turfs,
    totalAnchors: ROUTES.total_anchors,
    estHours: ROUTES.est_hours,
  };

  // Turf names and LDs come from the tenant's own catalog. A key the catalog
  // does not know is DROPPED rather than shown under its bare code: the route
  // rollups in `ROUTES` are still static Platte data, and pairing one with a
  // name this tenant never defined is exactly the leak being closed. While the
  // catalog is loading the list is empty, which reads as "no turf routes yet"
  // for a beat rather than flashing another county's neighbourhoods.
  const subregionCards = useMemo(() => {
    return Object.entries(ROUTES.subregions)
      .filter(([sr]) => !!regions.byKey[sr])
      .map(([sr, data]) => ({
        sr,
        ...(data as Record<string, unknown>),
        name: regions.byKey[sr].name,
        ld: regions.byKey[sr].ld,
      }));
  }, [regions]);

  const anchorTable = ROUTES.top_anchors.slice(0, 20);

  const handleDownloadRoute = async () => {
    if (!routeIdInput.trim()) {
      setDownloadError('Please enter a Route ID');
      return;
    }
    setDownloading(true);
    setDownloadError(null);
    try {
      const routeId = Number(routeIdInput);
      const blob = await downloadRouteCsv(routeId);
      triggerBrowserDownload(blob, `route-${routeId}.csv`);
      setRouteIdInput('');
    } catch (err: any) {
      const status = err?.response?.status;
      if (status === 403) {
        setDownloadError('Access denied. You do not have permission to export this route.');
      } else if (status === 404) {
        setDownloadError(`Route ${routeIdInput} not found.`);
      } else {
        setDownloadError(status ? `Error ${status}` : 'Failed to download route.');
      }
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <Breadcrumb items={[{ label: "County Map", action: "map" }, { label: "Campaign Operations", action: null }]} onNavigate={onDrill} />

      <ViewHeader title="Campaign Operations" tagline="Friendlies-First Route Planner" />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-bg-raised rounded-lg p-6 border border-border-base">
          <div className="text-sm text-fg-muted uppercase">Total Doors</div>
          <div className="text-3xl font-bold text-status-info mt-1 tabular-nums">{routeStats.totalDoors.toLocaleString()}</div>
        </div>
        <div className="bg-bg-raised rounded-lg p-6 border border-border-base">
          <div className="text-sm text-fg-muted uppercase">Total Turfs</div>
          <div className="text-3xl font-bold text-status-info mt-1 tabular-nums">{routeStats.totalTurfs.toLocaleString()}</div>
        </div>
        <div className="bg-bg-raised rounded-lg p-6 border border-border-base">
          <div className="text-sm text-fg-muted uppercase">Anchors</div>
          <div className="text-3xl font-bold text-status-info mt-1 tabular-nums">{routeStats.totalAnchors.toLocaleString()}</div>
        </div>
        <div className="bg-bg-raised rounded-lg p-6 border border-border-base">
          <div className="text-sm text-fg-muted uppercase">Est Hours</div>
          <div className="text-3xl font-bold text-status-info mt-1 tabular-nums">{(routeStats.estHours / 40).toFixed(1)} weeks</div>
        </div>
      </div>

      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <h2 className="text-lg font-bold text-fg-primary mb-4">Team Deployment Calculator</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div>
            <label htmlFor="routes-calc-volunteers" className="text-fg-muted text-sm font-semibold">Volunteers</label>
            <input id="routes-calc-volunteers" type="number" defaultValue={10} min={1} className="w-full bg-bg-base border border-border-base rounded py-2 px-3 text-fg-primary mt-2" />
          </div>
          <div>
            <label htmlFor="routes-calc-hours" className="text-fg-muted text-sm font-semibold">Hours/Person</label>
            <input id="routes-calc-hours" type="number" defaultValue={4} min={1} className="w-full bg-bg-base border border-border-base rounded py-2 px-3 text-fg-primary mt-2" />
          </div>
          <div>
            <label htmlFor="routes-calc-team-size" className="text-fg-muted text-sm font-semibold">Team Size</label>
            <input id="routes-calc-team-size" type="number" defaultValue={3} min={1} className="w-full bg-bg-base border border-border-base rounded py-2 px-3 text-fg-primary mt-2" />
          </div>
        </div>
        <p className="text-fg-muted text-sm mt-4">Estimated capacity: 400+ doors/day with strategic team deployment</p>
      </div>

      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <h2 className="text-lg font-bold text-fg-primary mb-4">Export Route CSV</h2>
        <div className="flex gap-3 items-end">
          <div className="flex-1">
            <label className="block text-fg-muted text-xs font-semibold uppercase mb-2">
              Route ID
            </label>
            <input
              type="text"
              value={routeIdInput}
              onChange={(e) => {
                setRouteIdInput(e.target.value);
                setDownloadError(null);
              }}
              placeholder="e.g., 123"
              className="w-full bg-bg-base border border-border-base rounded px-3 py-2 text-fg-primary text-sm"
              disabled={downloading}
            />
          </div>
          <button
            onClick={handleDownloadRoute}
            disabled={downloading}
            className="px-4 py-2 min-h-11 inline-flex items-center justify-center bg-status-info-bg hover:bg-status-info-bg/80 disabled:bg-border-subtle text-white font-semibold rounded transition text-sm"
          >
            {downloading ? 'Downloading...' : 'Download CSV'}
          </button>
        </div>
        {downloadError && (
          <div className="mt-2 text-sm text-status-danger bg-status-danger-bg/20 border border-status-danger/30 rounded px-3 py-2">
            {downloadError}
          </div>
        )}
      </div>

      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <h2 className="text-lg font-bold text-fg-primary mb-4">Classification Legend</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-bg-base rounded-lg p-4 border-l-4 border-tier-anchor">
            <div className="font-bold text-fg-primary">ANCHOR</div>
            <p className="text-fg-muted text-sm mt-2">Score 70+, proven supporter, rally point</p>
          </div>
          <div className="bg-bg-base rounded-lg p-4 border-l-4 border-tier-hot">
            <div className="font-bold text-fg-primary">HOT</div>
            <p className="text-fg-muted text-sm mt-2">Score 50-70, persuadable, high priority</p>
          </div>
          <div className="bg-bg-base rounded-lg p-4 border-l-4 border-tier-warm">
            <div className="font-bold text-fg-primary">WARM</div>
            <p className="text-fg-muted text-sm mt-2">Score 30-50, moveable, secondary target</p>
          </div>
          <div className="bg-bg-base rounded-lg p-4 border-l-4 border-tier-cool">
            <div className="font-bold text-fg-primary">COOL</div>
            <p className="text-fg-muted text-sm mt-2">Score 0-30, base, contact as time allows</p>
          </div>
        </div>
      </div>

      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <h2 className="text-lg font-bold text-fg-primary mb-4">Subregion Routes</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {subregionCards.map(card => (
            <button key={card.sr} onClick={() => onDrill("subregion", card.sr)} className="bg-bg-base hover:bg-bg-elevated rounded-lg p-4 border-l-4 transition cursor-pointer text-left" style={{ borderColor: LD_COLORS[card.ld] }}>
              <div className="font-bold text-fg-primary">{card.sr} — {card.name}</div>
              <div className="grid grid-cols-3 gap-2 mt-3 text-sm">
                <div><span className="text-fg-muted text-xs">Doors</span><br /><span className="font-bold text-fg-primary tabular-nums">{card.doors.toLocaleString()}</span></div>
                <div><span className="text-fg-muted text-xs">Turfs</span><br /><span className="font-bold text-fg-primary tabular-nums">{card.turfs}</span></div>
                <div><span className="text-fg-muted text-xs">Anchors</span><br /><span className="font-bold text-fg-primary tabular-nums">{card.anchors}</span></div>
              </div>
              <div className="mt-3 pt-3 border-t border-border-base text-xs">
                <span className="text-fg-muted">Classification:</span><br />
                <div className="flex gap-2 mt-1 text-xs">
                  <span>Hot: {card.hot}</span>
                  <span>Warm: {card.warm}</span>
                  <span>Cool: {card.cool}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      </div>

      <div className="bg-bg-raised rounded-xl p-6 border border-border-base">
        <h2 className="text-lg font-bold text-fg-primary mb-4">Top Anchors Directory</h2>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-base">
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">Name</th>
                <th className="text-left py-2 px-3 text-fg-muted font-semibold">Address</th>
                <th className="text-center py-2 px-3 text-fg-muted font-semibold">Score</th>
                <th className="text-center py-2 px-3 text-fg-muted font-semibold">Precinct</th>
                <th className="text-center py-2 px-3 text-fg-muted font-semibold">Subregion</th>
              </tr>
            </thead>
            <tbody>
              {anchorTable.map((anchor, i) => (
                <tr key={i} onClick={() => onDrill("precinct", anchor.precinct)} className="border-b border-border-base hover:bg-bg-elevated cursor-pointer transition">
                  <td className="py-2 px-3 text-fg-primary font-medium">{anchor.name}</td>
                  <td className="py-2 px-3 text-fg-secondary text-xs">{anchor.address}</td>
                  <td className="py-2 px-3 text-center font-bold text-tier-anchor tabular-nums">{anchor.score}</td>
                  <td className="py-2 px-3 text-center text-fg-secondary">{anchor.precinct}</td>
                  <td className="py-2 px-3 text-center text-fg-secondary font-semibold">{anchor.subregion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

    </div>
  );
};

